<template>
  <span class="modal-help">
    <button
      type="button"
      class="modal-help-btn"
      :class="{ 'modal-help-btn-labeled': showLabel, 'modal-help-btn-link': variant === 'link' }"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="panelId"
      :aria-label="label"
      :title="showLabel ? undefined : 'Help'"
      @click.stop.prevent="toggle"
    >
      <span v-if="!showLabel" aria-hidden="true">?</span>
      <span v-else class="modal-help-btn-text">help</span>
    </button>
    <teleport :to="teleportTo">
    <div
      v-if="open"
      :id="panelId"
      ref="panel"
      class="modal-help-sidebar"
      role="complementary"
      :aria-label="entry.title"
      @click.stop
    >
      <div class="modal-help-sidebar-head">
        <h2 class="modal-help-sidebar-title" tabindex="-1" ref="heading">{{ entry.title }}</h2>
        <button type="button" class="modal-help-close" aria-label="Close help" @click.stop.prevent="close">
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div class="modal-help-sidebar-body" ref="body">
        <p class="modal-help-intro">{{ entry.intro }}</p>
        <nav v-if="entry.sections.length > 1" class="modal-help-contents" aria-label="Help sections">
          <button
            v-for="(block, index) in entry.sections"
            :key="'jump-' + index"
            type="button"
            @click="goToSection(index)"
          >{{ block.heading }}</button>
        </nav>
        <section v-for="(block, index) in entry.sections" :key="index" :id="panelId + '-' + index" class="modal-help-section">
          <h3>{{ block.heading }}</h3>
          <p v-for="(paragraph, paragraphIndex) in block.paragraphs" :key="paragraphIndex">{{ paragraph }}</p>
          <ul v-if="block.items && block.items.length">
            <li v-for="(item, itemIndex) in block.items" :key="itemIndex">{{ item }}</li>
          </ul>
        </section>
      </div>
    </div>
    </teleport>
  </span>
</template>

<script>
  import { getModalHelp } from '../lib/modal-help'

  let nextPanelId = 0

  export default {
    name: 'ModalHelp',
    props: {
      topic: {
        type: String,
        required: true
      },
      label: {
        type: String,
        default: 'Help for this dialog'
      },
      showLabel: {
        type: Boolean,
        default: false
      },
      variant: {
        type: String,
        default: ''
      }
    },
    data () {

      return {
        open: false,
        panelId: `modal-help-panel-${++nextPanelId}`,
        teleportTo: 'body'
      }
    },
    computed: {
      entry () {

        return getModalHelp(this.topic) || {
          title: 'Help',
          intro: 'This dialog does not have a help topic yet.',
          sections: []
        }
      }
    },
    watch: {
      topic () {

        this.$nextTick(() => {

          if (this.$refs.body) this.$refs.body.scrollTop = 0
        })
      }
    },
    mounted () {

      this.onOtherHelp = (event) => {

        if (!event.detail || event.detail === this.panelId) return

        this.open = false
        this.releaseHelp()
      }

      this.onEscape = (event) => {

        if (!this.open || event.key !== 'Escape') return

        event.preventDefault()
        event.stopPropagation()
        this.close()
      }

      window.addEventListener('superturbo-modal-help', this.onOtherHelp)
      this.modalEl = this.$el.closest('.modal')

      if (this.modalEl) this.teleportTo = this.modalEl

      if (this.modalEl) {

        this.modalObserver = new MutationObserver(() => {

          if (this.open && !this.modalEl.classList.contains('show')) this.close()
        })

        this.modalObserver.observe(this.modalEl, { attributes: true, attributeFilter: ['class'] })
      }
    },
    beforeUnmount () {

      window.removeEventListener('superturbo-modal-help', this.onOtherHelp)
      this.releaseHelp()

      if (this.modalObserver) this.modalObserver.disconnect()
    },
    methods: {
      toggle () {

        if (this.open) this.close()
        else this.show()
      },
      show () {

        window.dispatchEvent(new CustomEvent('superturbo-modal-help', { detail: this.panelId }))
        this.open = true
        document.body.classList.add('modal-help-open')
        document.addEventListener('keydown', this.onEscape, true)

        this.$nextTick(() => {

          if (this.$refs.heading) this.$refs.heading.focus()
        })
      },
      close () {

        if (!this.open) return

        this.open = false
        this.releaseHelp()
        const button = this.$el && this.$el.querySelector('.modal-help-btn')

        if (button) button.focus()
      },
      releaseHelp () {

        document.removeEventListener('keydown', this.onEscape, true)
        document.body.classList.remove('modal-help-open')
      },
      goToSection (index) {

        const section = document.getElementById(`${this.panelId}-${index}`)

        if (section) section.scrollIntoView({ block: 'start' })
      }
    }
  }
</script>

<style>
/*
 * In a dialog, sit in the header's action row with the close button.
 * Bootstrap pulls .btn-close up by half the header padding. Match that
 * so the two controls share one center line.
 */
body .modal-header:has(.modal-help) {
  align-items: center;
}

body .modal-header .modal-title:has(.modal-help) {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0;
  line-height: 1.5;
}

body .modal-header .modal-help {
  position: static;
  top: auto;
  margin: 0 2px 0 auto;
  align-self: center;
}

body .modal-header .modal-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

body .modal-header:has(.modal-help) .btn-close {
  align-self: center;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  background-position: center;
  background-size: 16px;
}

.modal-help {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  margin: 0;
  line-height: 0;
  vertical-align: middle;
}

.modal-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #909090;
  font-size: 0.95rem;
  font-weight: 650;
  line-height: 1;
  cursor: pointer;
}

.modal-help-btn:hover,
.modal-help-btn:focus-visible {
  background: #222220;
  color: #e8e8e6;
  outline: 2px solid #9ad12b;
  outline-offset: 1px;
}

.modal-help-btn[aria-expanded="true"] {
  background: #2a2a27;
  color: #e8e8e6;
}

.modal-help-btn-labeled {
  box-sizing: border-box;
  width: auto;
  height: auto;
  min-height: 24px;
  padding: 4px 10px;
  gap: 0;
  border: 1px solid #333330;
  border-radius: 4px;
  background: transparent;
  color: #909090;
  font-size: 0.78rem;
  font-weight: 400;
  line-height: 1.5;
}

.modal-help-btn-labeled:hover,
.modal-help-btn-labeled[aria-expanded="true"] {
  background: #222220;
  color: #c0c0be;
  border-color: #484845;
  outline: none;
}

.modal-help-btn-link {
  border-width: 0;
  background: transparent;
  color: #b0b0ae;
  padding: 4px 10px;
}

.modal-help-btn-link:hover,
.modal-help-btn-link[aria-expanded="true"] {
  border-color: transparent;
  background: transparent;
  color: #e8e8e6;
}

.modal-help-btn-labeled:focus-visible {
  background: #222220;
  color: #c0c0be;
  border-color: #484845;
  outline: 2px solid #9ad12b;
  outline-offset: 1px;
}

#context-nav .modal-help-btn:not(.modal-help-btn-labeled),
.auth-topbar .modal-help-btn:not(.modal-help-btn-labeled) {
  width: 24px;
  height: 24px;
  font-size: 0.85rem;
  color: #b0b0ae;
}

.css-editor-header .modal-help-btn,
.api-modal-title .modal-help-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #7a7a78;
  font-size: 16px;
  line-height: 1;
}

.api-modal-title .modal-help {
  margin-left: auto;
}

.auth-topbar .modal-help {
  margin-left: auto;
}

@media (min-width: 960px) {
  body.modal-help-open .modal.show .modal-dialog {
    max-width: min(var(--bs-modal-width, 500px), calc(100vw - 28rem));
    margin-left: 1rem;
    margin-right: auto;
  }
}

.modal-help-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1080;
  width: min(400px, 100vw);
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #141412;
  color: #e8e8e6;
  border-left: 1px solid #333330;
  box-shadow: -16px 0 40px rgba(0, 0, 0, 0.45);
  text-align: left;
  font-weight: 400;
}

.modal-help-sidebar-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #2a2a28;
  background: #181916;
}

.modal-help-sidebar-title {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.3;
  color: #e8e8e6;
  font-weight: 650;
}

.modal-help-sidebar-title:focus {
  outline: 2px solid #9ad12b;
  outline-offset: 3px;
}

.modal-help-close {
  margin-left: auto;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #e8e8e6;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
}

.modal-help-close:hover,
.modal-help-close:focus {
  background: #2a2a28;
  outline: 2px solid #9ad12b;
  outline-offset: 2px;
}

.modal-help-sidebar-body {
  overflow: auto;
  padding: 16px 18px 32px;
}

.modal-help-intro {
  margin: 0 0 14px;
  color: #d5d5d2;
  line-height: 1.5;
}

.modal-help-contents {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0 0 18px;
}

.modal-help-contents button {
  border: 1px solid #333330;
  background: transparent;
  color: #e8e8e6;
  border-radius: 4px;
  font-size: 0.78rem;
  line-height: 1.2;
  padding: 4px 8px;
  cursor: pointer;
}

.modal-help-contents button:hover,
.modal-help-contents button:focus {
  border-color: #9ad12b;
  color: #9ad12b;
  outline: 2px solid #9ad12b;
  outline-offset: 1px;
}

.modal-help-section {
  margin: 0 0 18px;
}

.modal-help-section h3 {
  margin: 0 0 8px;
  font-size: 0.95rem;
  color: #9ad12b;
  font-weight: 650;
}

.modal-help-section p,
.modal-help-section li {
  margin: 0 0 8px;
  line-height: 1.5;
  color: #e8e8e6;
  font-size: 0.92rem;
}

.modal-help-section ul {
  margin: 0 0 8px;
  padding-left: 1.2rem;
}
</style>
